// Static brand mark — the canonical massing silhouette: a flowing
// terrain / wave form that reads as layered landscape (and doubles
// as the massing / topography motif).
//
// The mark renders at its NATURAL aspect ratio (≈ 1.957:1, wider
// than tall). Caller passes `size` as the WIDTH; the height follows
// from the viewBox so the form never gets squished or clipped.
//
// Gradient stops point at the active palette's --grad-bg-1…5 tokens
// (Daybreak, Aurora, Sunset, Forest, Mono, Ember all override these)
// so flipping palette from the admin page repaints the mark in
// lockstep with the wordmark + aurora background — without touching
// the mark's component itself.
//
// Direction-only shimmer (12 s loop) on x1/y1/x2/y2 keeps the mark
// alive without dragging the eye around the page. Stop-colour values
// stay rooted to the CSS variables so palette switches always win.

import { useRef } from 'react'

// Flowing terrain / wave silhouette. ViewBox 712.24 × 363.94.
const LOGO_PATH =
  'M448.07,188.65c-49.95,17.13-97.96,44.12-141.66,73.15-20.64,13.71-39.96,28.1-59.66,42.96-4.31,3.25-9.11,5.55-14.06,6.93-7.05,1.97-13.06-1.92-15.16-8.87-2.69-8.89-2.53-18.18-1.3-27.82,3.11-24.29,10.46-47.43,21.19-69.34,4.63-9.45,10.51-16.93,18.16-24.1,19.55-18.33,40.93-33.61,63.56-48.39-22.42,7.88-42.5,18.91-63.07,30.28-31.73,18.34-62.06,38.71-91.63,60.57C106.49,266.85,52.43,313.15,0,363.94l15.7-100.37c2.91-18.61,6.7-36.12,10.85-54.34,2.21-6.94,3.32-13.99,6.9-20.33,6.86-12.14,30.7-31.77,42.4-40.71,52.07-39.78,124.28-81.42,183.18-110.72,26.2-13.03,52.58-24.38,80.08-33.87,11.07-3.82,28.42-7.65,34.03,5.73,1.61,3.84,2.31,7.98,2.99,12.21.23,30.62-5.9,59.35-11.09,89.69,37.1-16.16,89.62-38.27,127.9-43.93,11.14-1.65,23.58-1.83,26.09,11.58.56,6.81.27,13.41-1.01,20.33-4.13,22.23-8.09,43.99-10.55,66.7,32.46-10.23,63.77-17.73,96.63-22.74,35.79-5.46,71.5-8.63,108.13-8.21-114.53,31.2-214.6,91.16-301.9,170.55-5.48,4.99-22.63,18.12-26.71,6.05s.39-34.66,3.54-47.02l7.16-28.04c2.75-6.15,6.41-11.27,10.93-16.34,12.3-13.12,27.17-22.48,42.81-31.52Z'

const VIEW_W = 712.24
const VIEW_H = 363.94

let MARK_COUNTER = 0
function newUid(): string {
  MARK_COUNTER += 1
  return `bm${MARK_COUNTER}`
}

export function BrandMark({
  size,
  className,
}: {
  // Width in pixels. Height follows the viewBox aspect so the mark
  // never gets squished — the bottom-of-mark clip in the header was
  // a consequence of forcing a square render box on this SVG.
  // When omitted, the width is driven by the className (eg a Tailwind
  // `w-[60px] sm:w-[120px]` for responsive sizing); inline `width`
  // would otherwise win against utility classes.
  size?: number
  className?: string
}) {
  const uid = useRef<string>(newUid()).current
  const gradId = `${uid}-grad`
  return (
    <svg
      className={className}
      style={{
        ...(size !== undefined ? { width: size } : {}),
        height: 'auto',
        aspectRatio: `${VIEW_W} / ${VIEW_H}`,
        overflow: 'visible',
        display: 'block',
      }}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      aria-hidden
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
          {/* Stops keyed to the active palette tokens so the mark
              picks up whichever palette the admin has selected. */}
          <stop offset="0%" stopColor="var(--grad-bg-1)" />
          <stop offset="28%" stopColor="var(--grad-bg-2)" />
          <stop offset="55%" stopColor="var(--grad-bg-3)" />
          <stop offset="78%" stopColor="var(--grad-bg-4)" />
          <stop offset="100%" stopColor="var(--grad-bg-5)" />
          {/* Quiet 12 s shimmer — gradient direction drifts so the
              fill has motion without distracting from the wordmark. */}
          <animate attributeName="x1" dur="12s" repeatCount="indefinite" values="0;0.2;0;-0.1;0" />
          <animate attributeName="y1" dur="12s" repeatCount="indefinite" values="0;0.3;0.1;0;0" />
          <animate attributeName="x2" dur="12s" repeatCount="indefinite" values="1;0.8;1;1.1;1" />
          <animate attributeName="y2" dur="12s" repeatCount="indefinite" values="1;0.7;0.9;1;1" />
        </linearGradient>
      </defs>
      <path d={LOGO_PATH} fill={`url(#${gradId})`} />
    </svg>
  )
}
